/**
 * This was taken from an existing useCountries implementation
 */
import { useMemo } from 'react';
import { request, useQuery } from '@applyboard/ui-utils';

interface RawCountriesResponse {
  data: Array<{
    id: string;
    attributes: {
      code: string;
      name: string;
    };
  }>;
}

export interface Country {
  code: string;
  name: string;
  id: string;
}

function isExpectedResponse(res: unknown): res is RawCountriesResponse {
  return Boolean(res && typeof res === 'object' && Array.isArray((res as { data: unknown }).data));
}

const defaultData: Country[] = [];

// Static countries/regions that are not available from the API for Shopfiy
const staticCountries: Country[] = [
  {
    id: 'EA',
    code: 'EA',
    name: 'East Africa',
  },
];

export function useCountries(
  options: { enabled?: boolean; refetchOnMount?: boolean | 'always' } = {},
) {
  const { isLoading, isFetching, error, data } = useQuery(
    ['useCountries', { pkg: 'ab-vas-gic' }],
    async () => {
      const response = await request('/api/v2/countries?sort=name', {
        defaultResponse: {
          data: [],
        },
        // eslint-disable-next-line no-restricted-syntax -- TODO: Refactor this to use `schema`
        isExpectedResponse,
      });

      const apiCountries = response.data.map<Country>(dataItem => ({
        id: dataItem.id,
        code: dataItem.attributes.code,
        name: dataItem.attributes.name,
      }));

      // Merge API countries with static countries
      return [...apiCountries, ...staticCountries];
    },
    {
      enabled: options.enabled,
      refetchOnMount: options.refetchOnMount,
    },
  );

  const countryToCodeMapping = useMemo(() => {
    if (!data) {
      return null;
    }

    return data.reduce(
      (acc, curr) => {
        acc[curr.code] = curr.name;
        return acc;
      },
      {} as { [countryCode: string]: string },
    );
  }, [data]);

  const countryToIdMapping = useMemo(() => {
    if (!data) {
      return null;
    }

    return data.reduce(
      (acc, curr) => {
        acc[curr.id] = curr.name;
        return acc;
      },
      {} as { [id: string]: string },
    );
  }, [data]);

  const countryToIdCodeMapping = useMemo(() => {
    if (!data) {
      return null;
    }

    return data.reduce(
      (acc, curr) => {
        acc[curr.id] = curr.code;
        return acc;
      },
      {} as { [id: string]: string },
    );
  }, [data]);

  return {
    isLoadingCountries: isLoading,
    isFetchingCountries: isFetching,
    countriesError: error,
    countries: data || defaultData,
    countryToCodeMapping,
    countryToIdMapping,
    countryToIdCodeMapping,
  };
}
